<template>
  <div class="p-search container">
    <div class="m-searchbar">
      <div class="m-searchbar__input-wrapper">
        <label for="search_keyword">搜尋</label>
        <input
          id="search_keyword"
          v-model="query.q"
          v-focus
          type="text"
          placeholder="請輸入關鍵字"
          @keyup.enter="searching"
        >
        <button v-show="query.q !== ''" class="m-searchbar__clear" title="清除關鍵字" @click="clearResults">
          ⊗ 清除
        </button>
      </div>
      <button class="m-searchbar__btn" title="使用關鍵字搜尋" aria-label="使用關鍵字搜尋" @click="searching">
        <title>使用關鍵字搜尋</title>
        <svg width="24" height="24">
          <path d="M23.635 21.86l-5.916-5.94a9.46 9.46 0 002.354-6.23c0-5.343-4.502-9.69-10.036-9.69S0 4.347 0 9.69c0 5.343 4.503 9.69 10.037 9.69 2.077 0 4.057-.605 5.75-1.753l5.961 5.985c.25.25.585.388.944.388.34 0 .662-.125.907-.353.52-.483.537-1.284.036-1.787zM10.037 2.528c4.09 0 7.418 3.213 7.418 7.162 0 3.95-3.328 7.162-7.418 7.162S2.618 13.64 2.618 9.69c0-3.95 3.328-7.162 7.419-7.162z" fill="#fff" fill-rule="nonzero" />
        </svg>
      </button>
    </div>

    <div class="p-search__body">
      <nav v-show="query.q !== '' && (datas !== null && datas.items.length > 0)" class="p-search__navbar">
        <ul class="p-search__navbar-list">
          <li v-for="category in categoryData" :key="category.name" :class="{'p-search__category--active': query.category === category.name}" class="p-search__category">
            <button :title="`關鍵字搜尋後，並依${category.name}類別進行過濾`" @click="goCategory(category.value)">
              {{ category.name }}
            </button>
          </li>
        </ul>
        <div class="p-search__navbar-control">
          <title>往後移動</title>
          <svg width="8" height="15">
            <path d="M7.712 8.243l-6.034 6.45a.938.938 0 01-1.39 0 1.102 1.102 0 010-1.486L5.628 7.5.287 1.793a1.102 1.102 0 010-1.485.939.939 0 011.39 0l6.034 6.45c.192.205.288.473.288.742s-.096.538-.288.743z" fill="#000" fill-rule="nonzero" />
          </svg>
        </div>
      </nav>
      <span class="p-search__result-stats">
        顯示結果第 {{ (datas !== null && datas.current_page - 1) * 20 + 1 }} ~ {{ (datas !== null && Math.min(datas.current_page * 20, datas.total)) }} 筆 / 關鍵字「<h1>{{ query.q.length > 6 ? query.q.slice(0, 6) + '...' : query.q }}</h1>」
      </span>
      <span v-if="datas !== null && datas.hot_tags.length > 0" class="p-search__result-stats">
        <span class="p-search__tags-label">更多相關新聞關鍵字：</span>
        <nuxt-link
          v-for="(tag, index) in displayedHotTags"
          :key="index"
          :to="`/search/${tag.trim()}/`"
          :title="`使用${tag.trim()}關鍵字前往搜尋頁並搜尋`"
          :rel="tag.length > 6 ? 'nofollow' : ''"
          class="e-tag l-tags__item search-tags"
        >
          {{ tag }}
        </nuxt-link>
        <button
          v-if="datas.hot_tags.length > 5"
          class="p-search__more-tags"
          @click="toggleTags"
        >
          {{ showAllTags ? '收合' : '更多' }}
        </button>
      </span>
      <div v-if="datas !== null && datas.items.length > 0" class="p-search__content">
        <nuxt-link
          v-for="i in datas.items"
          :key="i.id"
          :to="`/${i.type}/${i.id}/`"
          :title="`前往${i.type === 'video' ? '影音' : ''}新聞頁面`"
          class="m-card-s m-card-s--full p-search__result"
        >
          <div class="m-card-s__img-container">
            <div v-if="i.content_type === 'release' || i.content_type === 'sponsored'" class="e-type--bg m-card-s__type">
              <span :class="articleType(i).style" class="e-type">
                {{ articleType(i).text }}
              </span>
            </div>
            <LazyImage :src="selectImageSize(i.cover)" class="m-card-s__img" width="258" height="135" />
            <div v-if="i.type === 'video'" class="e-video__icon-wrapper">
              <div class="e-video__icon" />
            </div>
          </div>
          <div class="m-card-s__content">
            <h2 class="m-card-s__title m-card-s__title--lg" v-html="i.title" />
            <p class="m-card-s__article m-card--mle-3 m-card--mle-m-7" v-html="i.highlighting" />
            <div class="m-card-s__footer">
              <time :datetime="i.publish_at" class="e-time">
                <img src="https://static.ctwant.com/images/dist/clock.svg" class="m-card__clock" width="14" height="14" alt="發佈時間圖示">
                {{ i.publish_at }}
              </time>
              <span class="m-card-s__btn">
                繼續閱讀
              </span>
            </div>
          </div>
        </nuxt-link>
      </div>
    </div>

    <Pagination
      v-if="datas !== null && datas.items.length > 0 && datas.last_page > 1"
      :current-page="datas.current_page"
      :total-page="datas.last_page"
      @update="pageChange"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import articleHelpers from '@/utils/articleHelpers.js'
import Pagination from '@/components/Pagination.vue'
import LazyImage from '@/components/LazyImage.vue'

// 去除後端拿到被標記紅色的 title
const stripColorSpan = text => text.replace(/<span style="color:#e03f19;">(.*?)<\/span>/g, '$1')

export default {
  components: {
    Pagination,
    LazyImage
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus()
      }
    }
  },
  mixins: [articleHelpers],
  async asyncData({ $axios, $config, params, query, route, error, redirect }) {
    // 1. 取得網址參數 params.q，並去除前後空白
    if (params.q) {
      const trimmedParams = params.q.trim()
      // 2. 用正則去除《》和空白字元，把《》和空白移除
      const cleanedParams = trimmedParams.replace(/[《》\s]/g, '')
      // 3. 如果去除標點後的字串和原本不同，執行 301 轉址到乾淨網址
      if (cleanedParams !== trimmedParams) {
        // 301 永久轉址，SEO 友善，使用 encodeURIComponent 處理特殊字元
        return redirect(301, `/search/${encodeURIComponent(cleanedParams)}/`)
      }

      // 原本就有的：如果是 search/銀行存款?q=銀行存款 這種舊網址，也做 301
      if (query.q) {
        const trimmedQuery = query.q.trim()
        if (trimmedParams === trimmedQuery) {
          return redirect(301, `/search/${encodeURIComponent(trimmedParams)}/`)
        }
      }
    }

    const api = `https://${$config.mode !== 'production' ? 'beta-' : ''}api.ctwant.com/api/v2`

    const p = {
      params: {
        ...({ q: (params.q?.trim() || '') }),
        ...(route.query.page ? { page: route.query.page } : {}),
        ...(route.query.category ? { category: route.query.category } : {}),
        per_page: 20
      }
    }
    if (p.params.q.length >= 20) {
      p.params.q = p.params.q.trim().substr(0, 20)
    }

    try {
      const { data } = await $axios.$get(`${api}/search`, p)

      if (data.total === 0 && p.params.q !== '') {
        error({ statusCode: 404 })
      }
      const obj = {
        q: route.params.q.trim()
      }
      if (route.query.category) {
        obj.category = route.query.category
      }
      return {
        datas: data,
        query: obj
      }
    } catch (e) {
      console.error('Search: ', e)
      error({ statusCode: 404 })
    }
  },
  data() {
    return {
      categoryData: [{
        name: '全部',
        value: ''
      }, {
        name: '社會',
        value: '社會'
      }, {
        name: '娛樂',
        value: '娛樂'
      }, {
        name: '財經',
        value: '財經'
      }, {
        name: '政治',
        value: '政治'
      }, {
        name: '漂亮',
        value: '漂亮'
      }, {
        name: '生活',
        value: '生活'
      }, {
        name: '國際',
        value: '國際'
      }],
      showAllTags: false,
      datas: null,
      query: {
        q: '',
        category: '',
        page: 1
      }
    }
  },
  head() {
    const hostAndNeedBackslashPath = this.HOST + (this.$route.params && this.$route.params.q ? `/search/${encodeURIComponent(this.$route.params.q.trim())}/` : `${this.$route.path}/`)
    const title = (this.datas !== null && this.datas.items.length > 0) ? `「${this.query.q}」相關新聞 | CTWANT` : '新聞搜尋 | CTWANT'
    const description = (this.datas !== null && this.datas.items.length > 0) ? this.datas.items.map(item => stripColorSpan(item.title)).slice(0, 3).join(', ') : title
    const BreadcrumbList = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': this.HOST,
            name: 'CTWANT'
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': hostAndNeedBackslashPath,
            name: title
          }
        }
      ]
    }

    const headObj = {
      title,
      meta: [
        { hid: 'ogUrl', property: 'og:url', content: (this.datas !== null && this.datas.items.length > 0) ? hostAndNeedBackslashPath : `${this.HOST}/search/` },
        { hid: 'ogTitle', property: 'og:title', content: `「${this.query.q}」相關新聞 | CTWANT` },
        { hid: 'description', name: 'description', content: description },
        { hid: 'ogDescription', property: 'og:description', content: description }
      ],
      link: [
        { hid: 'alternate', rel: 'alternate', hreflang: 'zh-Hant', href: (this.datas !== null && this.datas.items.length > 0 && this.query.q.length <= 8) ? hostAndNeedBackslashPath : `${this.HOST}/search/` }
        // { hid: 'canonical', rel: 'canonical', href: (this.datas !== null && this.datas.items.length > 0 && this.query.q.length <= 8) ? hostAndNeedBackslashPath : `${this.HOST}/search/` }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        { innerHTML: JSON.stringify(BreadcrumbList), type: 'application/ld+json' }
      ]
    }
    // 如果 關鍵字數字大於6  or  Item筆數小於3  or  page大於1頁  or  有包含category的搜尋  or  datas.total 參數小於 10 就不建立索引 及 不建立 canonical
    if (
      this.query.q.length > 6 ||
      this.$route.query.page ||
      this.$route.query.category ||
      (this.datas !== null && this.datas.tagged_count < 3) ||
      // this.datas.items.reduce((count, item) => count + (item.tags.includes(this.query.q) ? 1 : 0), 0) < 5
      (this.datas !== null && this.datas.total < 10)
    ) {
      headObj.meta.push({ hid: 'robots', name: 'robots', content: 'noindex, nofollow' })
    } else {
      // headObj.link.push({ hid: 'alternate', rel: 'alternate', hreflang: 'zh-Hant', href: (this.datas !== null && this.datas.items.length > 0 && this.query.q.length <= 8) ? hostAndNeedBackslashPath : `${this.HOST}/search/` })
      headObj.link.push({ hid: 'canonical', rel: 'canonical', href: (this.datas !== null && this.datas.items.length > 0 && this.query.q.length <= 8) ? hostAndNeedBackslashPath : `${this.HOST}/search/` })
    }
    return headObj
  },
  computed: {
    ...mapGetters([
      'HOST',
      'WIDTH_PAD',
      'screenWidth'
    ]),
    displayedHotTags() {
      if (!this.datas || !this.datas.hot_tags) {
        return []
      }
      return this.showAllTags ? this.datas.hot_tags : this.datas.hot_tags.slice(0, 5)
    },
    selectImageSize() {
      const sw = this.screenWidth
      return function (imageArr) {
        let imgUrl = null

        if (sw <= this.WIDTH_PAD) {
          imgUrl = imageArr.sm
        } else {
          imgUrl = imageArr.md
        }
        return imgUrl
      }
    }
  },
  watchQuery: true,
  mounted() {
    const vm = this
    let isHideAd = 'N'
    let isOtherAd = 'N'
    if (this.datas !== null) {
      for (const i in this.datas.items) {
        if (i.is_hide_ad === 'Y') {
          isHideAd = 'Y'
        }
        if (i.is_hide_ad === 'Y') {
          isOtherAd = 'Y'
        }
      }
    }
    this.$nextTick(() => {
      this.$store.dispatch('ad/displayAd', {
        isHideAd,
        isOtherAd
      })
      this.$store.dispatch('ad/registerAndDisplayPopupAd', {
        isHideAd
      })
      vm.$gtm.push({
        event: 'domMounted',
        pageUrl: vm.$route.fullPath,
        pageTitle: vm.query.q === '' ? '搜尋' : `「${vm.query.q}」相關新聞`
      })
    })
  },
  methods: {
    toggleTags() {
      this.showAllTags = !this.showAllTags
    },
    searching() {
      if (this.query.q === '') {
        this.$notyf.error('請輸入搜尋關鍵字')
        return
      }
      if (this.query.q.length > 20) {
        this.$notyf.error('搜尋關鍵字請少於 20 個字')
        return
      }
      this.query.q = this.query.q.trim()
      const { q, page, category } = this.query

      const obj = {
        path: `/search/${q}/`,
        query: {
          ...(page && page !== 1 ? { page } : {}),
          ...(category ? { category } : {})
        }
      }
      this.$router.push(obj)
    },
    goCategory(value) {
      this.$router.push({
        path: this.$route.path,
        query: {
          page: 1,
          ...(value ? { category: value } : {})
        }
      })
    },
    pageChange(num) {
      // this.query.page = num
      // this.searching()
    },
    clearResults() {
      this.query.q = {
        q: '',
        category: '',
        page: 1
      }
      this.datas = null
      this.$router.push({
        path: '/search'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/scss/pages/_search';

.p-search__more-tags {
  display: inline-block;
  padding: 9px 16px;
  margin-left: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  font-size: 14px;
  color: #000;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
  @include pad{
    margin-bottom: 12px;
    margin-left: 0;
  }
}
</style>
